// Simple audio manager for low-latency SFX playback
// - Uses Web Audio API when available (preloaded & decoded)
// - Falls back to HTMLAudioElement if decoding unavailable
// - Unlocks audio on first user gesture to avoid autoplay delays

let audioCtx = null;
let unlocked = false;
const bufferCache = new Map(); // url -> AudioBuffer
const htmlAudioCache = new Map(); // url -> HTMLAudioElement
let lastPlayAt = 0;

function getCtx() {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioCtx = new Ctx();
  }
  return audioCtx;
}

export function setupAudioUnlock({ prefetch = ['/sfx/win.mp3', '/sfx/loose.mp3'] } = {}) {
  if (typeof window === 'undefined') return;
  if (unlocked) return;
  const unlock = async () => {
    const ctx = getCtx();
    try { if (ctx && ctx.state === 'suspended') await ctx.resume(); } catch { /* ignore */ }
    unlocked = true;
    // Preload & decode common sfx
    prefetch.forEach((url) => { void loadBuffer(url); });
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    window.removeEventListener('touchstart', unlock, { capture: true });
  };
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true, passive: true });
  window.addEventListener('touchstart', unlock, { once: true, passive: true, capture: true });
}

async function loadBuffer(url) {
  if (!url) return null;
  if (bufferCache.has(url)) return bufferCache.get(url);
  const ctx = getCtx();
  if (!ctx) return null;
  try {
    const resp = await fetch(url, { cache: 'force-cache' });
    const arr = await resp.arrayBuffer();
    const buf = await ctx.decodeAudioData(arr);
    bufferCache.set(url, buf);
    return buf;
  } catch {
    return null;
  }
}

function getHtmlAudio(url) {
  if (htmlAudioCache.has(url)) return htmlAudioCache.get(url);
  const el = new Audio(url);
  el.preload = 'auto';
  htmlAudioCache.set(url, el);
  return el;
}

export async function playWin({ url = '/sfx/win.mp3', volume = 0.12 } = {}) {
  const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  if (now - lastPlayAt < 200) return; // prevent double-trigger in StrictMode
  lastPlayAt = now;

  const ctx = getCtx();
  try {
    if (ctx) {
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch { /* ignore */ } }
      let buf = bufferCache.get(url);
      if (!buf) buf = await loadBuffer(url);
      if (buf) {
        const src = ctx.createBufferSource();
        const gain = ctx.createGain();
        gain.gain.value = Math.max(0, Math.min(1, volume ?? 0.7));
        src.buffer = buf;
        src.connect(gain).connect(ctx.destination);
        src.start(0);
        return;
      }
    }
  } catch {
    // fall through to HTMLAudio
  }
  // Fallback
  try {
    const audio = getHtmlAudio(url);
    audio.currentTime = 0;
    audio.volume = Math.max(0, Math.min(1, volume ?? 0.7));
    const p = audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    // ignore
  }
}

export async function playLose({ url = '/sfx/loose.mp3', volume = 0.05 } = {}) {
  const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  if (now - lastPlayAt < 200) return; // prevent double-trigger
  lastPlayAt = now;

  const ctx = getCtx();
  try {
    if (ctx) {
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch { /* ignore */ } }
      let buf = bufferCache.get(url);
      if (!buf) buf = await loadBuffer(url);
      if (buf) {
        const src = ctx.createBufferSource();
        const gain = ctx.createGain();
        gain.gain.value = Math.max(0, Math.min(1, volume ?? 0.21));
        src.buffer = buf;
        src.connect(gain).connect(ctx.destination);
        src.start(0);
        return;
      }
    }
  } catch {
    // fallback below
  }
  try {
    const audio = getHtmlAudio(url);
    audio.currentTime = 0;
    audio.volume = Math.max(0, Math.min(1, volume ?? 0.21));
    const p = audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    // ignore
  }
}
